import { useCallback, useEffect, useRef, useState } from "react";
import { getGhgDatabases, getModules } from "../KPIGrid/kpiGridApi";

export const FILTER_OPTIONS_ERROR = "Couldn't load the module and source filters.";

/**
 * @param {{ isSuccess?: boolean, data?: { data?: object[] } } | undefined} res an apiCall response
 * @returns {object[]|null} its rows, or null when the call failed
 */
export const rowsOf = (res) => (res?.isSuccess === false || !Array.isArray(res?.data?.data) ? null : res.data.data);

/**
 * Modules and GHG databases for the page's filters and row labels. Each list
 * that loads is kept, and a failure of either is reported, so the page can
 * still render its factors and say what is missing.
 *
 * @returns {{ modules: object[], databases: object[], error: string, reload: () => Promise<void> }}
 */
export function useFilterOptions() {
  const [modules, setModules] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setError("");
    try {
      const [moduleRes, databaseRes] = await Promise.all([getModules(), getGhgDatabases()]);
      if (fetchId !== fetchIdRef.current) return;
      const moduleRows = rowsOf(moduleRes);
      const databaseRows = rowsOf(databaseRes);
      if (moduleRows) setModules(moduleRows);
      if (databaseRows) setDatabases(databaseRows);
      if (!moduleRows || !databaseRows) setError(FILTER_OPTIONS_ERROR);
    } catch {
      if (fetchId === fetchIdRef.current) setError(FILTER_OPTIONS_ERROR);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [load]);

  return { modules, databases, error, reload: load };
}
